/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useRef } from "react";
import { View } from "react-native";
import { useLazyQuery, useMutation } from "@apollo/client";
import md5 from "blueimp-md5";
import { Redirect, useLocation } from "react-router-dom";
import jsforce from "jsforce";
import qs from "qs";
import SwipeableViews from "react-swipeable-views";
import { useAuth0, GetTokenWithPopupOptions } from "@auth0/auth0-react";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import { LoadingView } from "UpduoLibrary";
import { skaleWhite } from "config/UpduoColors";
import OrgCodePane from "./OrgCodePane";
import SwitchPane from "./SwitchPane";
import ConfirmationPane from "./ConfirmationPane";
import FinalPane from "./FinalPane";
import BrandingArea from "./BrandingArea";
import { SkalePrimaryApolloClient } from "config/UpduoApolloClient";
import { callApi } from "config/UpduoService";
import { logoutResetState, instantiatePrimaryUser } from "config/ResetState";
import { login, loginWithKey } from "config/Login";
import {
  OrganizationType,
  PrimaryJoinOrgMutation,
  UserProxyType,
  SummitType,
} from "graphql/internal/generated";
import { GET_ORGANIZATION, GET_USERPROXY } from "graphql/internal/Queries";
import { PRIMARY_JOIN_ORG } from "graphql/internal/Mutations";
import * as UpduoAnalytics from "config/UpduoAnalytics";

const client = `web_${md5(window.navigator.appVersion)}`;

export default function StartRoute({ match, history }) {
  const primaryAuthToken = useSelector(
    (state: RootState) => state.primaryAuthToken
  );
  const authToken = useSelector((state: RootState) => state.authToken);

  const startObject = useSelector((state: RootState) => state.startObject);
  const startObjectRef = useRef<any>();
  startObjectRef.current = startObject;

  const dispatch = useDispatch();
  const location = useLocation() as any; // TODO fix router types

  const [loginValidationText, setLoginValidationText] = useState("");
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);

  const [emailValidationText, setEmailValidationText] = useState("");
  const [passwordValidationText, setPasswordValidationText] = useState("");
  const [paneIndex, setPaneIndex] = useState(0);

  const { getAccessTokenWithPopup, isAuthenticated, logout } = useAuth0();

  const [primaryJoinOrg, {}] = useMutation<PrimaryJoinOrgMutation>(
    PRIMARY_JOIN_ORG,
    {
      fetchPolicy: "no-cache", // this line is needed in order to proceed
      client: SkalePrimaryApolloClient,
      onCompleted: () => {
        _handleAuthProceed();
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-start-primaryjoinorg-error", {
          error,
        });
        console.error("ERROR primaryJoinOrg", error);
      },
    }
  );

  const [
    getOrganization,
    { loading: getOrganizationLoading, error: getOrganizationError },
  ] = useLazyQuery<{ organization: OrganizationType }>(GET_ORGANIZATION, {
    context: { isPublic: true },
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    onCompleted: (data) => {
      if (!data?.organization) {
        return;
      }
      const newStartObject = Object.assign({}, startObject, {
        orgCode: data?.organization?.orgCode,
        orgJoinRule: data?.organization?.orgJoinRule,
        name: data?.organization?.name,
        auth0ConnectionName: data?.organization?.auth0ConnectionName,
      });
      dispatch(ActionCreators.setStartObject(newStartObject));
      if (data?.organization?.orgCode && data?.organization?.orgJoinRule) {
        setPaneIndex(1); // Go to SwitchPane (2nd pane)
      }
    },
  });

  const [
    getUserProxy,
    { loading: getUserProxyLoading, error: getUserProxyError },
  ] = useLazyQuery<{ userproxy: UserProxyType }>(GET_USERPROXY, {
    context: { isPublic: true },
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    onCompleted: (data) => {
      if (!data?.userproxy) {
        return;
      }
      if (
        data?.userproxy?.firstName &&
        data?.userproxy?.lastName &&
        data?.userproxy?.email
      ) {
        const newStartObject = Object.assign({}, startObject, {
          firstName: data?.userproxy?.firstName,
          lastName: data?.userproxy?.lastName,
          title: data?.userproxy?.title,
          email: data?.userproxy?.email,
          internalId: data?.userproxy?.internalId,
          userExists: data?.userproxy?.userExists,
        });
        dispatch(ActionCreators.setStartObject(newStartObject));
        if (
          data?.userproxy?.firstName &&
          data?.userproxy?.lastName &&
          data?.userproxy?.email
        ) {
          setPaneIndex(2); // Go to ConfirmationPane
        }
      }
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      logout({ returnTo: window.location.origin }); // ensure that Auth0 users have ephemeral logins
    }
    document.body.style.backgroundColor = skaleWhite;
    dispatch(ActionCreators.setSelectedSummit({} as SummitType));
    const splitUrlArray = window.location.href.split("#");
    const urlTail = splitUrlArray[splitUrlArray.length - 1];
    if (urlTail.startsWith("access_token")) {
      const queryObject = qs.parse(urlTail);
      callApi(
        "postLoginSalesforce",
        {
          access_token: queryObject["access_token"],
          key: "https://login.salesforce.com",
          client,
        },
        (responseJson: { key: string }) => {
          loginWithKey(
            responseJson.key,
            _handleLoginReset,
            startObjectRef.current?.orgCode
          );
        },
        _handleLoginReset
      );
    }
  }, []);

  const _handleLoginPress = async (email: string, password: string) => {
    UpduoAnalytics.logEvent("user-home-login", { email });
    setIsLoginInProgress(true);
    try {
      await login(
        email,
        password,
        _handleLoginReset,
        startObjectRef.current?.orgCode
      );
    } catch (errorObject: any) {
      _handleLoginReset;
    }
  };

  const _handleSignupPress = (
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ) => {
    // Post user sign up
    UpduoAnalytics.logEvent("user-signup-submit", {
      email,
      first_name: firstName,
      last_name: lastName,
    });
    callApi(
      "postSignUp",
      {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
      },
      () => {
        _handleLoginPress(email, password);
      },
      (errorJson) => {
        errorJson.then((errorObject) => {
          let reason = "";
          // Prioritize handling of email and password issues first
          if ("email" in errorObject) {
            reason = errorObject["email"][0];
            setEmailValidationText(reason);
          } else if ("password" in errorObject) {
            reason = errorObject["password"][0];
            setPasswordValidationText(reason);
          }
          UpduoAnalytics.logEvent("user-signup-failure", {
            email,
            first_name: firstName,
            last_name: lastName,
            reason,
          });
        });
      }
    );
  };

  const _handleSkip = (type = "none") => {
    UpduoAnalytics.logEvent("user-start-skip", { type });
    setPaneIndex(1);
    dispatch(ActionCreators.setStartObject({}));
  };

  const _handleFacebookPress = (response) => {
    callApi(
      "postLoginFacebook",
      { access_token: response.accessToken, client },
      (responseJson: { key: string }) => {
        loginWithKey(
          responseJson.key,
          _handleLoginReset,
          startObjectRef.current?.orgCode
        );
      },
      _handleLoginReset
    );
  };

  const _handleGooglePress = (response) => {
    console.log("Google Login Response", response);
    callApi(
      "postLoginGoogle",
      { access_token: response.access_token, client },
      (responseJson: { key: string }) => {
        loginWithKey(
          responseJson.key,
          _handleLoginReset,
          startObjectRef.current?.orgCode
        );
      },
      _handleLoginReset
    );
  };

  const _handleAuth0Press = async () => {
    try {
      const options = {
        scope: "openid email profile",
        prompt: "login",
        ignoreCache: true,
      } as GetTokenWithPopupOptions;
      if (startObjectRef.current?.auth0ConnectionName) {
        Object.assign(options, {
          connection: startObjectRef.current.auth0ConnectionName,
        });
      }
      const accessToken = await getAccessTokenWithPopup(options);
      callApi(
        "postLoginAuth0",
        { access_token: accessToken, client },
        (responseJson: { key: string }) => {
          loginWithKey(
            responseJson.key,
            _handleLoginReset,
            startObjectRef.current?.orgCode
          );
        },
        _handleLoginReset
      );
    } catch (error) {
      console.log("ERROR _handleAuth0Press", error);
    }
  };

  const _handleSalesforcePress = () => {
    const redirectUri = window.location.href;
    // @ts-ignore
    jsforce.browser.init({
      clientId: process.env.REACT_APP_SALESFORCE_CLIENT_ID,
      redirectUri,
      proxyUrl: redirectUri,
      httpProxy: redirectUri,
    });
    // @ts-ignore
    jsforce.browser.login();
  };

  const _handleLoginReset = async (errorPromise) => {
    let newLoginValidationText = "Incorrect e-mail and/or password.";
    logoutResetState();
    setIsLoginInProgress(false);
    if (errorPromise) {
      const errorObject = await Promise.resolve(errorPromise);
      if (errorObject["non_field_errors"]) {
        newLoginValidationText = errorObject["non_field_errors"].join(" ");
      } else if (errorObject["detail"]) {
        newLoginValidationText = errorObject["detail"];
      }
    }
    setLoginValidationText(newLoginValidationText);
  };

  const _handleAuthProceed = () => {
    let queryOrgCode;
    if (startObjectRef.current?.orgCode) {
      queryOrgCode = startObjectRef.current?.orgCode;
    } else if (history.location?.state?.referrer?.search) {
      queryOrgCode = new URLSearchParams(
        history.location.state.referrer.search
      ).get("org_code");
    }
    instantiatePrimaryUser({
      failureCallback: () => _handleLoginReset(null),
      successCallback: () => {
        // if log in is just an intermediary step (ie. coming from an email link to a summit detail page)
        if (history.location.pathname) {
          history.replace(history.location.pathname);
        } else history.push("/");
      },
      noOrgCallback: () => {
        history.push("/join_organization");
      },
      orgCode: queryOrgCode ? queryOrgCode : match.params.orgCode,
    });
  };

  const _handleBackPress = () => {
    setPaneIndex((previousValue) =>
      previousValue > 1 ? previousValue - 1 : 0
    );
  };

  useEffect(() => {
    // Get logged-in user's info and instantiate widget
    if (primaryAuthToken) {
      if (match.params.orgCode) {
        primaryJoinOrg({
          variables: {
            primaryJoinOrgRequest: {
              orgCode: match.params.orgCode,
            },
          },
        });
      } else {
        _handleAuthProceed();
      }
    }
  }, [primaryAuthToken, match.params.orgCode, _handleAuthProceed]);

  if (primaryAuthToken && authToken) {
    if (
      location.state?.referrer?.pathname &&
      location.state?.referrer?.redirect
    ) {
      if (location.state.referrer.rsvpRequest) {
        return (
          <Redirect
            to={{
              pathname: location.state.referrer.pathname,
              search: "?rsvpSuccess=true",
            }}
          />
        );
      } else return <Redirect to={location.state.referrer.pathname} />;
    } else return <Redirect to={{ pathname: "/" }} />;
  } else if (isLoginInProgress || primaryAuthToken) {
    return <LoadingView />;
  }
  return (
    <View
      style={{
        alignItems: "stretch",
        backgroundColor: skaleWhite,
        flexDirection: "row",
        minHeight: "100vh",
      }}
    >
      <BrandingArea />
      <SwipeableViews
        index={paneIndex}
        style={{
          flex: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          alignSelf: "stretch",
        }}
        className={"grow-children scroll-grandchildren"}
      >
        <OrgCodePane
          isLoading={getOrganizationLoading}
          onPress={(orgCode: string) => {
            getOrganization({ variables: { orgCode } });
          }}
          onSkip={() => _handleSkip("orgCode")}
          hasError={!!getOrganizationError}
        />
        <SwitchPane
          onBack={_handleBackPress}
          isLoading={getUserProxyLoading}
          onSubmitInternalId={(internalId: string, orgCode: string) => {
            getUserProxy({ variables: { internalId, orgCode } });
          }}
          onSignupPress={_handleSignupPress}
          onLoginPress={_handleLoginPress}
          onFacebookPress={_handleFacebookPress}
          onGooglePress={_handleGooglePress}
          onAuth0Press={_handleAuth0Press}
          onSalesforcePress={_handleSalesforcePress}
          loginValidationText={loginValidationText}
          isLoginInProgress={isLoginInProgress}
          emailValidationText={emailValidationText}
          passwordValidationText={passwordValidationText}
          hasError={!!getUserProxyError}
        />
        <ConfirmationPane
          onBack={_handleBackPress}
          onLoginPress={_handleLoginPress}
          loginValidationText={loginValidationText}
          isLoginInProgress={isLoginInProgress}
          onConfirmationPress={() => {
            setPaneIndex(3);
          }}
        />
        <FinalPane
          onBack={_handleBackPress}
          onSignupPress={_handleSignupPress}
          emailValidationText={emailValidationText}
          passwordValidationText={passwordValidationText}
        />
      </SwipeableViews>
    </View>
  );
}
